import { useState } from 'react';

import {
  Button,
  Divider,
  Flex,
  type FlexProps,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Spacer,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '@/components';
import {
  IconCalendarOutlineGray,
  IconEditPencilOutline,
  IconHourOutline,
  IconRightArrow,
} from '@/components/Icons/icons-generated';
import {
  PopoverDayPicker,
  type PopoverDayPickerProps,
  PopoverTimePicker,
} from '@/components/Popover';
import type { TimeOnly } from '@/lib/time';

interface DetailBoxProps extends Omit<FlexProps, 'title'> {
  title: string;
  dateTime?: string;
  borderColor?: string;
  onSubmitDateTime?: (date: Date) => void | Promise<void>;
  notBefore?: PopoverDayPickerProps['notBefore'];
  notAfter?: PopoverDayPickerProps['notAfter'];
  isLoading?: boolean;
  isDisabled?: boolean;
}

type DateOrTime = 'date' | 'time';

export const DetailBoxWithDateEditor: React.FC<DetailBoxProps> = ({
  title,
  dateTime,
  borderColor = 'gray.50',
  onSubmitDateTime,
  notAfter,
  notBefore,
  isLoading,
  isDisabled,
  ...rest
}) => {
  const { t } = useTranslation(['common']);
  const [dateOrTime, setDateOrTime] = useState<DateOrTime>('date');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<TimeOnly | null>(null);
  const modifiedValue = formatDate(dateTime);
  const parentPopoverDisclosure = useDisclosure();
  const nestedPopoverDisclosure = useDisclosure({
    onClose: parentPopoverDisclosure.onClose,
  });

  const handleCloseAllPopovers = () => {
    parentPopoverDisclosure.onClose();
    nestedPopoverDisclosure.onClose();
  };

  const handlePopoverSwitch = (_dateOrTime: DateOrTime) => {
    setDateOrTime(_dateOrTime);
    if (!nestedPopoverDisclosure.isOpen) {
      nestedPopoverDisclosure.onOpen();
    }
  };

  const onSubmitTime = async (time: TimeOnly) => {
    const newDateTime = dateTime ? new Date(dateTime) : new Date();
    newDateTime.setHours(time.getHours());
    newDateTime.setMinutes(time.getMinutes());

    await onSubmitDateTime?.(newDateTime);
    handleCloseAllPopovers();
  };

  const onSubmitDate = async (date: Date | undefined) => {
    if (!date) return;

    const newDateTime = dateTime ? new Date(dateTime) : new Date();
    const newDate = date;
    newDate.setHours(newDateTime.getHours());
    newDate.setMinutes(newDateTime.getMinutes());

    await onSubmitDateTime?.(newDate);
    handleCloseAllPopovers();
  };

  return (
    <Popover
      closeOnEsc
      closeOnBlur={false}
      placement="bottom-start"
      returnFocusOnClose={false}
      isOpen={parentPopoverDisclosure.isOpen}
      onClose={handleCloseAllPopovers}
    >
      <PopoverAnchor>
        <Flex
          boxShadow={
            parentPopoverDisclosure.isOpen ? '0 0 0 4px #F0E9F8' : 'none'
          }
          direction="column"
          py={3}
          px={4}
          w="full"
          justifyContent="space-between"
          h={18}
          borderWidth={1}
          borderRadius="lg"
          borderColor={borderColor}
          {...rest}
        >
          <Flex align="center">
            <Text fontWeight="light" fontSize="sm">
              {title}
            </Text>
            <Spacer />
            <Button
              p={0}
              size="20px"
              border="none"
              variant="link"
              isDisabled={isDisabled}
              onClick={
                parentPopoverDisclosure.isOpen
                  ? handleCloseAllPopovers
                  : parentPopoverDisclosure.onOpen
              }
            >
              <IconEditPencilOutline fontSize="20px" />
            </Button>
          </Flex>
          <Spacer />
          <Text fontSize="sm" fontWeight="semibold">
            {modifiedValue ? modifiedValue : '-'}
          </Text>
        </Flex>
      </PopoverAnchor>
      <PopoverContent w="200px" borderRadius="xl" borderColor="gray.100">
        <PopoverBody p={0}>
          <Popover
            closeOnBlur
            placement="right-start"
            isOpen={nestedPopoverDisclosure.isOpen}
            onClose={nestedPopoverDisclosure.onClose}
          >
            <PopoverAnchor>
              <Flex direction="column">
                <Flex
                  align="center"
                  px="14px"
                  py="12px"
                  onClick={() => handlePopoverSwitch('date')}
                >
                  <IconCalendarOutlineGray fontSize="20px" mr={2} />
                  <Text>{t('common:timeAndDate.date')}</Text>
                  <Spacer />
                  <IconRightArrow fontSize="20px" />
                </Flex>
                <Divider />
                <Flex
                  align="center"
                  px="14px"
                  py="12px"
                  onClick={() => handlePopoverSwitch('time')}
                >
                  <IconHourOutline fontSize="20px" mr={2} />
                  <Text>{t('common:timeAndDate.time')}</Text>
                  <Spacer />
                  <IconRightArrow fontSize="20px" />
                </Flex>
              </Flex>
            </PopoverAnchor>
            {dateOrTime === 'date' ? (
              <PopoverDayPicker
                wrappedInPopoverContent
                isLoading={isLoading}
                notBefore={notBefore}
                notAfter={notAfter}
                dayPickerMode="single"
                singleDayValue={date}
                setSingleDayValue={(singleDayValue) => setDate(singleDayValue)}
                onSubmit={(singleDayValue) => onSubmitDate(singleDayValue)}
              />
            ) : (
              <PopoverTimePicker
                value={time}
                onChange={setTime}
                onSubmit={onSubmitTime}
                isLoading={isLoading}
              />
            )}
          </Popover>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
